import { useCallback, useEffect, useState } from 'react'
import type { QueuedRegistration } from '@/lib/offline/offlineDb'
import { readAllMyContacts, saveAllMyContacts } from '@/features/registrations/myContactsCache'
import { outbox, type Outbox } from '@/features/registrations/outbox'
import { registrationService, RegistrationServiceError, type MyContact, type RegistrationService } from '@/features/registrations/registrationService'
import { useSync } from '@/features/registrations/SyncProvider'

/**
 * Everyone the signed-in person has registered, at every event: those still on
 * this device and those the server holds. Offline, or if loading fails, the
 * copy saved the last time the list loaded is used, so the list is still there.
 */
export type MyContactsState = Readonly<{
  loading: boolean
  contacts: MyContact[]
  onDevice: QueuedRegistration[]
  error: string | null
  /** Set when `contacts` is the device's saved copy rather than fresh from the server. */
  savedAt: string | null
}>

export type MyContactsSources = Readonly<{
  service?: Partial<Pick<RegistrationService, 'listAllMine' | 'countAllMine' | 'getMine'>>
  queue?: Pick<Outbox, 'forCollector'>
}>

export function useMyContacts(userId: string | null, { service: given, queue = outbox }: MyContactsSources = {}) {
  const { changes } = useSync()
  const [state, setState] = useState<MyContactsState>({ loading: true, contacts: [], onDevice: [], error: null, savedAt: null })
  const [reloads, setReloads] = useState(0)

  useEffect(() => {
    if (!userId) return
    let active = true
    const service = { ...registrationService, ...given }
    const local = queue.forCollector(userId).catch(() => [] as QueuedRegistration[])
    const remote = service.listAllMine(userId).then(
      (contacts) => {
        void saveAllMyContacts(userId, contacts).catch(() => undefined)
        return { contacts, error: null as string | null, savedAt: null as string | null }
      },
      async (cause: unknown) => {
        const error = cause instanceof RegistrationServiceError ? cause.message : 'Your contacts could not be loaded.'
        const copy = await readAllMyContacts(userId).catch(() => undefined)
        return { contacts: copy?.contacts ?? [], error, savedAt: copy?.cachedAt ?? null }
      },
    )
    void Promise.all([local, remote]).then(([onDevice, loaded]) => {
      if (active) setState({ loading: false, onDevice, ...loaded })
    })
    return () => { active = false }
  }, [userId, given, queue, changes, reloads])

  const reload = useCallback(() => {
    setState((current) => ({ ...current, loading: true }))
    setReloads((count) => count + 1)
  }, [])

  /** Applies a saved change (status, ticket) to one contact, here and in the device's copy. */
  const update = useCallback((registrationId: string, change: Partial<MyContact>) => {
    setState((current) => {
      const contacts = current.contacts.map((contact) => contact.registrationId === registrationId ? { ...contact, ...change } : contact)
      if (userId) void saveAllMyContacts(userId, contacts).catch(() => undefined)
      return { ...current, contacts }
    })
  }, [userId])

  /** Drops a removed registration, sent or still on the device. */
  const drop = useCallback((id: string) => {
    setState((current) => {
      const contacts = current.contacts.filter((contact) => contact.registrationId !== id)
      if (userId) void saveAllMyContacts(userId, contacts).catch(() => undefined)
      return { ...current, contacts, onDevice: current.onDevice.filter((record) => record.clientRegistrationId !== id) }
    })
  }, [userId])

  return { ...state, total: state.contacts.length + state.onDevice.length, reload, update, drop }
}

/**
 * How many people this person has registered, for the Outreach tab's card:
 * a count from the server rather than the whole list, plus any still on the
 * device. Offline it falls back to the saved copy's length.
 */
export function useMyContactsCount(userId: string | null, { service: given, queue = outbox }: MyContactsSources = {}): number | null {
  const { changes } = useSync()
  const [count, setCount] = useState<number | null>(null)
  useEffect(() => {
    if (!userId) return
    let active = true
    const service = { ...registrationService, ...given }
    const local = queue.forCollector(userId).then((records) => records.length, () => 0)
    const remote = Promise.resolve().then(() => service.countAllMine(userId)).catch(async () => (await readAllMyContacts(userId).catch(() => undefined))?.contacts.length ?? null)
    void Promise.all([local, remote]).then(([onDevice, sent]) => { if (active) setCount(sent === null && onDevice === 0 ? null : (sent ?? 0) + onDevice) })
    return () => { active = false }
  }, [userId, given, queue, changes])
  return count
}

export type MyContactState = Readonly<{ loading: boolean; contact: MyContact | null; onDevice: QueuedRegistration | null; error: string | null }>

/**
 * One contact for its page: fetched on its own rather than with the whole
 * list, from the device's saved copy when offline, or from the registrations
 * still waiting on this device.
 */
export function useMyContact(userId: string | null, id: string, { service: given, queue = outbox }: MyContactsSources = {}) {
  const [state, setState] = useState<MyContactState>({ loading: true, contact: null, onDevice: null, error: null })
  useEffect(() => {
    if (!userId) return
    let active = true
    const service = { ...registrationService, ...given }
    const fromDevice = async () => (await readAllMyContacts(userId).catch(() => undefined))?.contacts.find((contact) => contact.registrationId === id) ?? null
    void (async () => {
      const onDevice = (await queue.forCollector(userId).catch(() => [] as QueuedRegistration[])).find((record) => record.clientRegistrationId === id) ?? null
      if (onDevice) { if (active) setState({ loading: false, contact: null, onDevice, error: null }); return }
      try {
        const contact = await service.getMine(id, userId)
        if (active) setState({ loading: false, contact, onDevice: null, error: null })
      } catch (cause) {
        const contact = await fromDevice()
        if (active) setState({ loading: false, contact, onDevice: null, error: contact ? null : (cause instanceof RegistrationServiceError ? cause.message : 'This contact could not be loaded.') })
      }
    })()
    return () => { active = false }
  }, [userId, id, given, queue])

  /** Applies a saved change here and in the device's copy of the list. */
  const update = useCallback((change: Partial<MyContact>) => {
    setState((current) => current.contact ? { ...current, contact: { ...current.contact, ...change } } : current)
    if (userId) void patchSavedCopy(userId, (contacts) => contacts.map((contact) => contact.registrationId === id ? { ...contact, ...change } : contact))
  }, [userId, id])

  /** Takes a removed registration out of the device's copy too. */
  const drop = useCallback(() => {
    if (userId) void patchSavedCopy(userId, (contacts) => contacts.filter((contact) => contact.registrationId !== id))
  }, [userId, id])

  return { ...state, update, drop }
}

async function patchSavedCopy(userId: string, change: (contacts: MyContact[]) => MyContact[]) {
  try {
    const saved = await readAllMyContacts(userId)
    if (saved) await saveAllMyContacts(userId, change(saved.contacts), undefined, new Date(saved.cachedAt))
  } catch { /* the saved copy is best effort */ }
}
